import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiClose, mdiCog } from "@mdi/js";
import { useTheme } from "@mui/system";
import { useAddUpdateLaborRateViewModel } from "./useAddUpdateLaborRateViewModel";

const laborRateSchema = z.object({
  name: z.string().min(1, "Name is required."),
  rate: z.number().min(0, "Labor rate must be a non-negative number."),
});

export const AddUpdateLaborRate = ({
  selectedLaborRate,
  open,
  onClose,
  onRefresh = () => {},
}) => {
  const theme = useTheme();
  const {
    control,
    reset,
    setValue,
    getValues,
    formState: { isValid, isDirty },
  } = useForm({
    mode: "onChange",
    defaultValues: { name: "", rate: "" },
    resolver: zodResolver(laborRateSchema),
  });

  const { isLoading, createLaborRate, updateLaborRate } =
    useAddUpdateLaborRateViewModel({
      onRefresh,
      onClose,
      reset,
    });

  const fields = [
    {
      label: "Name",
      name: "name",
      required: true,
      formControl: control,
      width: "100%",
    },
    {
      label: "Labor Rate",
      name: "rate",
      fieldType: INPUT_TYPES.CURRENCY,
      required: true,
      value: getValues("rate"),
      formControl: control,
      width: "100%",
      setValue: (rawValue) => {
        setValue(`rate`, Number(rawValue), {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
  ];

  const handleCancel = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    reset(selectedLaborRate || { name: "", rate: "" });
  }, [selectedLaborRate]);

  const handleSave = () => {
    const data = getValues();
    if (selectedLaborRate) {
      updateLaborRate(data);
    } else {
      createLaborRate(data);
    }
  };

  return (
    <MiModal
      title={`${selectedLaborRate ? "Edit" : "Add"} Labor Rate`}
      variant="temporary"
      open={open}
      loading={isLoading}
      closeModal={handleCancel}
      titleIcon={mdiCog}
      leftIconColor={theme.palette.primary.main}
      rightIcon={mdiClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleCancel,
        },
        {
          name: selectedLaborRate ? "Edit Labor Rate" : "Add Labor Rate",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          disabled: !isDirty || !isValid || isLoading,
          onClick: handleSave,
        },
      ]}
    >
      <MiDetailFields detailFields={fields} />
    </MiModal>
  );
};
